/**
 * Reports Service
 * Handles fetching executive reports for shops
 */

import { apiClient } from './client';
import { createLogger } from '../config/environment';

const log = createLogger('ReportsService');

export interface ShopReportItem {
  shopId: string;
  shopName: string;
  isLive: boolean;
  hasActiveSubscription: boolean;
  totalCatalogs: number;
}

export interface ShopReportsResponse {
  totalShops: number;
  liveShops: number;
  offlineShops: number;
  shopsWithActiveSubscriptions: number;
  shopsWithNoSubscriptions: number;
  shops: ShopReportItem[];
}

export interface PartnerSubscription {
  subscriptionId: string;
  planName: string;
  planId: string;
  planStartDate: string;
  planEndDate: string;
  isActive: boolean;
  createdAt: string;
}

export interface PartnerShop {
  shopId: string;
  shopName: string;
  ownerName: string;
  address: string;
  creationDate: string;
  subscriptions: PartnerSubscription[];
}

export interface PartnerReportsResponse {
  partnerId: string;
  totalShops: number;
  totalSubscriptions: number;
  shops: PartnerShop[];
}

class ReportsService {
  /**
   * Get executive reports for all shops
   * @returns Shop reports data with statistics
   */
  async getShopReports(): Promise<ShopReportsResponse> {
    try {
      log.info('Fetching shop reports');
      const response = await apiClient.get<ShopReportsResponse>('/api/reports/shops');
      log.info('✅ Shop reports fetched successfully', {
        totalShops: response.data.totalShops,
        liveShops: response.data.liveShops,
        offlineShops: response.data.offlineShops,
      });
      return response.data;
    } catch (error) {
      log.error('Failed to fetch shop reports:', error);
      throw error;
    }
  }

  /**
   * Get partner reports and statistics
   * @param partnerId Partner ID to fetch reports for
   * @returns Partner reports data with shops and subscriptions
   */
  async getPartnerReports(partnerId: string): Promise<PartnerReportsResponse> {
    try {
      log.info('Fetching partner reports', { partnerId });
      const response = await apiClient.get<PartnerReportsResponse>(`/api/reports/partner/${partnerId}`);
      log.info('✅ Partner reports fetched successfully', {
        partnerId: response.data.partnerId,
        totalShops: response.data.totalShops,
        totalSubscriptions: response.data.totalSubscriptions,
      });
      return response.data;
    } catch (error) {
      log.error('Failed to fetch partner reports:', error);
      throw error;
    }
  }
}

export const reportsService = new ReportsService();

